import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "buttonText", "spinner"]
  static values = {
    activityId: String,
    inloopId: String,
    statusUrl: String,
    generateUrl: String,
    outcomeUrl: String
  }

  connect() {
    // Check if generation is already in progress
    this.checkStatus()
  }

  generate(event) {
    event.preventDefault()

    // Check if button text is "View Insights" - if so, just redirect
    if (this.hasButtonTextTarget && this.buttonTextTarget.textContent === "View Insights") {
      window.location.href = this.outcomeUrlValue
      return
    }

    // Show loading state
    this.showLoading()

    // Send POST request to generate outcomes
    fetch(this.generateUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          // Start polling for job status
          this.startPolling()

          // Show success toast
          if (window.toastController) {
            window.toastController.success("Generating test insights...")
          }
        } else {
          this.hideLoading()
          if (window.toastController) {
            window.toastController.error(data.error || "Failed to generate insights")
          }
        }
      })
      .catch(error => {
        console.error("Error generating insights:", error)
        this.hideLoading()
        if (window.toastController) {
          window.toastController.error("Failed to generate insights")
        }
      })
  }

  checkStatus() {
    if (!this.statusUrlValue) return

    fetch(this.statusUrlValue, {
      headers: {
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.status === "processing") {
          this.showLoading()
          this.startPolling()
        } else if (data.status === "completed") {
          this.hideLoading()
          this.showCompletedState()
        }
      })
      .catch(error => {
        console.error("Error checking status:", error)
      })
  }

  startPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
    }

    this.pollingInterval = setInterval(() => {
      this.pollStatus()
    }, 2000) // Poll every 2 seconds
  }

  pollStatus() {
    if (!this.statusUrlValue) return

    fetch(this.statusUrlValue, {
      headers: {
        "Accept": "application/json"
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.status === "completed") {
          this.stopPolling()
          this.hideLoading()
          this.showCompletedState()

          // Show success toast
          if (window.toastController) {
            window.toastController.success("Test insights generated successfully!")
          }

          // Redirect to insights page after short delay
          setTimeout(() => {
            window.location.href = this.outcomeUrlValue
          }, 1000)
        } else if (data.status === "failed") {
          this.stopPolling()
          this.hideLoading()

          if (window.toastController) {
            window.toastController.error("Failed to generate insights")
          }
        }
      })
      .catch(error => {
        console.error("Error polling status:", error)
        this.stopPolling()
        this.hideLoading()
      })
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  showLoading() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.classList.add("opacity-75", "cursor-not-allowed")
    }

    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = "Generating..."
    }

    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
    }
  }

  hideLoading() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false
      this.buttonTarget.classList.remove("opacity-75", "cursor-not-allowed")
    }

    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = "Generate Insights"
    }

    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
    }
  }

  showCompletedState() {
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = "View Insights"
    }

    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = false
      this.buttonTarget.classList.remove("opacity-75", "cursor-not-allowed")
    }
  }

  disconnect() {
    this.stopPolling()
  }
}
